import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import Gauge from "@/components/dashboard/GaugeChart";
import KpiCard from "@/components/dashboard/KpiCard";
import SubsystemMatrix, { StatusPill, SubsystemNameCell, SUBSYSTEM_COLORS } from "@/components/dashboard/SubsystemMatrix";
import { Wrench, AlertTriangle, Clock, Timer } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, ReferenceLine, Cell } from "recharts";

function Panel({ n, title, children, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center mb-4">
        <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
        <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
      </div>
      {children}
    </div>
  );
}

// bucket delay minutes into distribution
const BUCKETS = [
  { max: 1, label: "< 1 min" },
  { max: 2, label: "1–2 min" },
  { max: 5, label: "2–5 min" },
  { max: 10, label: "5–10 min" },
  { max: 30, label: "10–30 min" },
  { max: Infinity, label: "≥ 30 min" },
];

export default function Maintainability() {
  const { params } = useFilters();
  const [summary, setSummary] = useState(null);
  const [failures, setFailures] = useState([]);
  const [matrix, setMatrix] = useState({ items: [], targets: {} });
  const [trend, setTrend] = useState({ items: [] });

  useEffect(() => {
    (async () => {
      const [s, f, t, mx] = await Promise.all([
        api.get("/metrics/summary", { params }),
        api.get("/failures", { params: { ...params, limit: 5000 } }),
        api.get("/metrics/trend", { params }),
        api.get("/metrics/subsystem_matrix", { params }),
      ]);
      setSummary(s.data); setFailures(f.data.items); setTrend(t.data); setMatrix(mx.data);
    })();
  }, [params.start, params.end, params.line, params.subsystem]);

  const c = summary?.current || {};
  const prev = summary?.previous;
  const sett = summary?.settings || {};
  const target = sett.target_mttr_minutes || 25;

  const distribution = useMemo(() => {
    const buckets = BUCKETS.map(b => ({ label: b.label, count: 0 }));
    failures.forEach(f => {
      const m = (f.delay_seconds || 0) / 60;
      for (let i = 0; i < BUCKETS.length; i++) {
        if (m < BUCKETS[i].max) { buckets[i].count++; break; }
      }
    });
    return buckets;
  }, [failures]);

  const mttrBySubsystem = useMemo(() => {
    const map = new Map();
    failures.forEach(f => {
      const key = f.equipment || "Unknown";
      const g = map.get(key) || { equipment: key, total: 0, sum: 0 };
      g.total++; g.sum += (f.delay_seconds || 0) / 60;
      map.set(key, g);
    });
    return Array.from(map.values()).map(g => ({ equipment: g.equipment, mttr: g.total > 0 ? Number((g.sum / g.total).toFixed(2)) : 0 })).sort((a, b) => b.mttr - a.mttr);
  }, [failures]);

  const mttrTrend = useMemo(() => {
    // approximate: average delay in each bucket
    const buckets = new Map();
    failures.forEach(f => {
      if (!f.tanggal) return;
      let key;
      if (trend.granularity === "monthly") key = f.tanggal.slice(0, 7) + "-01";
      else if (trend.granularity === "weekly") {
        const d = new Date(f.tanggal);
        const monday = new Date(d); monday.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
        key = monday.toISOString().slice(0, 10);
      } else key = f.tanggal;
      const g = buckets.get(key) || { key, sum: 0, n: 0 };
      g.sum += (f.delay_seconds || 0) / 60; g.n++;
      buckets.set(key, g);
    });
    return Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key))
      .map(g => ({ key: g.key, MTTR: Number((g.sum / g.n).toFixed(2)) }));
  }, [failures, trend]);

  const fmt = (k) => {
    const d = new Date(k);
    if (trend.granularity === "monthly") return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="space-y-6 pb-8" data-testid="detail-maintainability">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-3 bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow flex flex-col justify-center items-center">
          <Gauge label="MTTR Maintainability" value={c.mttr_minutes} max={Math.max(target * 2, c.mttr_minutes || 0)} target={target} unit="Minutes" direction="down_good" testId="maint-gauge" />
        </div>
        <div className="col-span-12 md:col-span-9 grid grid-cols-2 md:grid-cols-3 gap-4">
          <KpiCard testId="kpi-mttr-m" label="MTTR (Minutes)" value={c.mttr_minutes} prev={prev?.mttr_minutes} direction="down_good" icon={Wrench} iconColor="#3b82f6" iconBg="#dbeafe" prevLabel="prev period" />
          <KpiCard testId="kpi-events-m" label="Failure Events" value={c.total_failure_events} prev={prev?.total_failure_events} direction="down_good" icon={AlertTriangle} iconColor="#f59e0b" iconBg="#fef3c7" prevLabel="prev period" />
          <KpiCard testId="kpi-delay-m" label="Recovery Time Total" value={c.total_delay_minutes} unit="min" prev={prev?.total_delay_minutes} direction="down_good" icon={Timer} iconColor="#3b82f6" iconBg="#dbeafe" prevLabel="prev period" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Panel n="1" title="Recovery Time Distribution" className="lg:col-span-5">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={distribution} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" name="Events" radius={[6, 6, 0, 0]} maxBarSize={44}>
                {distribution.map((d, i) => (
                  <Cell key={`bucket-${d.label ?? i}`} fill={i < 3 ? "#22c55e" : i < 5 ? "#f59e0b" : "#ef4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel n="2" title="MTTR by Subsystem" className="lg:col-span-7">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={mttrBySubsystem} layout="vertical" margin={{ top: 10, right: 30, left: 20, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} unit=" min" />
              <YAxis type="category" dataKey="equipment" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={100} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} cursor={{ fill: "#f1f5f9" }} />
              <ReferenceLine x={target} stroke="#ef4444" strokeDasharray="4 3" label={{ value: `Target ${target}`, fill: "#ef4444", fontSize: 10, position: "top" }} />
              <Bar dataKey="mttr" name="Avg MTTR (min)" fill="#3b82f6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel n="3" title="MTTR Trend vs Target">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={mttrTrend.map(t => ({ ...t, key: fmt(t.key) }))} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} unit=" min" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <ReferenceLine y={target} stroke="#ef4444" strokeDasharray="5 3" label={{ value: `Target ${target}min`, fill: "#ef4444", fontSize: 10, position: "right" }} />
            <Line type="monotone" dataKey="MTTR" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4, fill: "#3b82f6" }} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <SubsystemMatrix
        testId="maintainability-subsystem-matrix"
        panelIndex="4"
        title="Matriks Maintainability per Subsistem"
        subtitle="MTTR = rata-rata waktu pemulihan (menit) per subsistem"
        target={{ label: `Target ≤ ${matrix.targets?.mttr_minutes ?? target} menit` }}
        items={matrix.items}
        columns={[
          { key: "subsystem", label: "Subsistem", render: (v) => <SubsystemNameCell name={v} colorMap={SUBSYSTEM_COLORS} /> },
          { key: "events", label: "Total Events", align: "right" },
          { key: "downtime_min", label: "Total Recovery (menit)", align: "right", render: (v) => v.toFixed(2) },
          { key: "avg_mttr_min", label: "Avg MTTR (menit)", align: "right", render: (v) => v.toFixed(2), tone: (v) => (v <= (matrix.targets?.mttr_minutes ?? target) ? "text-emerald-700 font-bold" : "text-rose-700 font-bold") },
          { key: "_status", label: "Status", align: "right",
            render: (_, row) => {
              const goal = matrix.targets?.mttr_minutes ?? target;
              if (row.events === 0) return <StatusPill neutral />;
              return <StatusPill ok={row.avg_mttr_min <= goal} />;
            } },
        ]}
      />
    </div>
  );
}
